import { WorksDisputeFiltersQueryCgResponse } from '@common/graphql';
import { gql, TypedDocumentNode } from '@common/hooks';
import {
  DisputeListItemFragment,
  DisputeListItemFragmentType,
  DisputeRejectionReasonFragment,
  DisputeRejectionReasonFragmentType,
  DisputeReviewFragment,
  DisputeReviewFragmentType,
} from '@pay-bill/graphql/fragments';

export const DISPUTE_REVIEW_QUERY: TypedDocumentNode<
  DisputeReviewQueryResponse,
  DisputeReviewQueryArgs
> = gql`
  query DisputeReviewQuery($code: String!) {
    dispute(code: $code) {
      ...DisputeReviewFragment
    }
  }
  ${DisputeReviewFragment}
`;

export type DisputeReviewQueryResponse = {
  dispute: DisputeReviewFragmentType;
};

type DisputeReviewQueryArgs = {
  code: string;
};

export const DISPUTE_PREVIEW_INFO_QUERY: TypedDocumentNode<
  DisputePreviewInfoResponse,
  DisputePreviewInfoArg
> = gql`
  query DisputePreviewInfo(
    $finalRateCents: Int
    $skipRates: Boolean = false
    $newHours: Float!
    $newDate: ISO8601Date!
    $newPayCode: String!
    $staffingRequestAssignmentId: ID!
  ) {
    previewRate: worksDisputeCalculatedRate(
      finalRateCents: $finalRateCents
      newHours: $newHours
      newDate: $newDate
      newPayCode: $newPayCode
      staffingRequestAssignmentId: $staffingRequestAssignmentId
    ) {
      billingAmountCents @skip(if: $skipRates)
      finalRateCents
    }
    disputeMatches(
      input: {
        newHours: $newHours
        newPayCode: $newPayCode
        newDate: $newDate
        finalRateCents: $finalRateCents
        staffingRequestAssignmentId: $staffingRequestAssignmentId
      }
    ) {
      dispute {
        id
        code
        worksInvoice {
          id
        }
        worksReverseInvoice {
          id
        }
      }
      invoiceItem {
        id
        date
        hours
        totalSeconds
        employeeId
        payCode
        worksInvoice {
          id
          code
        }
        worksReverseInvoice {
          id
          code
        }
      }
    }
  }
`;

export type DisputePreviewInfoResponse = {
  previewRate: {
    billingAmountCents: number;
    finalRateCents: number;
  };
  disputeMatches: {
    dispute: {
      id: string;
      code: string;
      worksInvoice: {
        id: string;
      };
      worksReverseInvoice: {
        id: string;
      };
    };
    invoiceItem: {
      id: string;
      date: string;
      hours: number;
      employeeId: string;
      payCode: string;
      worksInvoice: {
        id: string;
        code: string;
      };
      worksReverseInvoice: {
        id: string;
        code: string;
      };
    };
    workerTimesheet: {
      errorMessage: string;
    };
  };
};

type DisputePreviewInfoArg = {
  skipRates?: boolean;
  newHours?: number;
  newDate: string;
  newPayCode: string;
  finalRateCents?: number;
  staffingRequestAssignmentId: string;
};

export type PaginatedDisputesQueryArgs = {
  limit: number;
  offset: number;
  orderDirection: 'ASC' | 'DESC';
  orderField:
    | 'NEEDED_ACTIONS'
    | 'INV_CODE'
    | 'RINV_CODE'
    | 'CODE'
    | 'DATE'
    | 'DIFFERENTIAL'
    | 'DISPUTE_TYPE'
    | 'EMPLOYEE_ID'
    | 'EMPLOYEE_NAME'
    | 'COST_CENTER'
    | 'LAST_ACTION_AT'
    | 'STATE'
    | 'SUPPLIER';
  filters: {
    code?: string[];
    employeeId?: string[];
    employeeName?: string[];
    invoiceCode?: string[];
    revInvoiceCode?: string[];
    state?: string[];
    exported?: boolean;
    costCenter?: string[];
    supplierName?: string[];
  };
};

export const PAGINATED_DISPUTES_QUERY: TypedDocumentNode<
  PaginatedDisputesQueryResponse,
  PaginatedDisputesQueryArgs
> = gql`
  query PaginatedDisputesList(
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: DisputeOrderField
    $filters: PaginatedDisputesFilterInput
  ) {
    paginatedDisputes(
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
      filters: $filters
    ) {
      totalCount
      disputes {
        id
        ...DisputeListItemFragment
      }
    }
  }

  ${DisputeListItemFragment}
`;

type PaginatedDisputesQueryResponse = {
  paginatedDisputes: {
    totalCount: number;
    disputes: DisputeListItemFragmentType[];
  };
};

export const DISPUTE_FILTERS_QUERY: TypedDocumentNode<WorksDisputeFiltersQueryCgResponse> = gql`
  query WorksDisputeFilters {
    disputeFilters {
      states
    }
  }
`;

export const DISPUTE_REJECTION_OPTIONS_QUERY: TypedDocumentNode<DisputeRejectionOptionsQueryResponse> = gql`
  query DisputeRejectionOptionsQuery {
    options: disputeRejectionOptions {
      rejectionReasons {
        ...DisputeRejectionReasonFragment
      }
    }
  }
  ${DisputeRejectionReasonFragment}
`;

type DisputeRejectionOptionsQueryResponse = {
  options: {
    rejectionReasons: DisputeRejectionReasonFragmentType[];
  };
};
